/**
 * Integrytas Group — Email Distribution System (EDS)
 * Page: EDS.CAMPAIGN.LIST  —  Campaign Management
 *
 * Grid + right-side drawer with three tabs:
 *   Info        — internal_code, template, status, from overrides, schedule
 *   Translation — per-language campaign name / description
 *   Lists       — assign / remove recipient lists for this campaign
 *
 * Status workflow: draft → scheduled → sending → sent/cancelled/paused
 * is_terminal flag on eds_campaign_status prevents editing terminal campaigns.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useLanguageOptions } from '../../lib/use_language_options';
import { apiDb } from '../../lib/api_client';
import { hasAccess, getForensicActorId  } from '../../types/auth';
import { registerChildEditor, type ChildEditorProps } from '../../lib/child_editors';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { enqueueJob } from '../../services/eds/EdsSendService';
import { SortableHeader } from '../../components/common/SortableHeader';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { RowActionsMenu } from '../../components/common/RowActionsMenu';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import type {
  EdsCampaignRow,
  EdsCampaignTlRow,
  EdsCampaignStatusRow,
  EdsEmailTemplateRow,
  EdsRecipientListRow,
  EdsCampaignSortKey,
  EdsCampaignTlForm,
} from '../../types/eds';

const PAGE_CODE = 'EDS.CAMPAIGN.LIST';
registerUdcHost(PAGE_CODE, 'eds_campaign', 'Campaigns');

const FILTER_KEY = 'eds.campaigns.filter';
const STATUS_KEY = 'eds.campaigns.status';
const INTERNAL_CODE_RE = /^[A-Z][A-Z0-9_.]*$/;

const GRID_COLS: GridColumnDef[] = [
  { key: 'campaign_name',    header: 'Campaign',    defaultWidth: 200 },
  { key: 'internal_code',    header: 'Code',        defaultWidth: 160 },
  { key: 'template_code',    header: 'Template',    defaultWidth: 160 },
  { key: 'status_code',      header: 'Status',      defaultWidth: 100 },
  { key: 'scheduled_send_at', header: 'Scheduled',  defaultWidth: 140 },
  { key: 'completed_at',     header: 'Completed',   defaultWidth: 140 },
];

// Campaign-status → badge variant (label comes localized from eds_campaign_status_tl).
const STATUS_VARIANT: Record<string, StatusVariant> = {
  draft:      'neutral',
  scheduled:  'info',
  sending:    'info',
  sent:       'success',
  paused:     'warning',
  cancelled:  'danger',
};

function emptyTl(language_id: string): EdsCampaignTlForm {
  return { language_id, campaign_name: '', campaign_description: '' };
}

const CAMPAIGN_INFO_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code',       type: 'text',    label: 'Internal Code', required: true, col_span: 1 },
  { key: 'status_code',         type: 'select',  label: 'Status',        col_span: 1 },
  { key: 'template',            type: 'select',  label: 'Template',      required: true, col_span: 2 },
  { key: 'from_name_override',  type: 'text',    label: 'From Name Override',  col_span: 1 },
  { key: 'from_email_override', type: 'text',    label: 'From Email Override', col_span: 1 },
  { key: 'scheduled_send_at',   type: 'text',    label: 'Scheduled Send At',   col_span: 2 },
];
const CAMPAIGN_INFO_CORE_LAYOUT = toUldCoreLayout(CAMPAIGN_INFO_FIELD_DEFS);

export interface EdsCampaignListPageProps { session: AuthSession; }

export function EdsCampaignListPage({ session }: EdsCampaignListPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <EdsCampaignListPageInner session={session} />
    </PersonalizationProvider>
  );
}

function EdsCampaignListPageInner({ session }: EdsCampaignListPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]               = useState<EdsCampaignRow[]>([]);
  const [filter, setFilter]           = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facets (status, template).
  const [facets, setFacets]           = useState<{ status: string[]; template: string[] }>(
    () => {
      const saved = sessionStorage.getItem(STATUS_KEY);
      return { status: saved ? [saved] : [], template: [] };
    },
  );
  const [statuses, setStatuses]       = useState<EdsCampaignStatusRow[]>([]);
  // §17.B2: status_code → localized display_text from eds_campaign_status_tl.
  const [statusLabels, setStatusLabels] = useState<Record<string, string>>({});
  const [editing, setEditing]         = useState<EdsCampaignRow | null>(null);
  const [creating, setCreating]       = useState(false);
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [enqueueing, setEnqueueing]   = useState<string | null>(null);
  const [enqueueMsg, setEnqueueMsg]   = useState<{ id: string; ok: boolean; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EdsCampaignRow | null>(null);
  const sort = useSort<EdsCampaignSortKey>('eds.campaigns.sort', { key: 'campaign_name', dir: 'asc' });
  const detail = useExpandedRows<string>();
  const udcGrid = useUdcGrid('eds_campaign', PAGE_CODE, { pkColumn: 'campaign_id', rows: rows as unknown as Record<string, unknown>[] });

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);
  useEffect(() => { sessionStorage.setItem(STATUS_KEY, facets.status[0] ?? ''); }, [facets.status]);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_campaign')
        .select(`
          *,
          eds_campaign_tl!left(campaign_name, language_id),
          eds_email_template!left(internal_code, from_name_default)
        `)
        .order('created_at', { ascending: false });
      if (e) throw e;
      type CampaignTl = TlRowText & { campaign_name?: string };
      type Row = EdsCampaignRow & {
        eds_campaign_tl?: CampaignTl[];
        eds_email_template?: { internal_code: string; from_name_default: string } | null;
      };
      const flat = ((data ?? []) as Row[]).map(r => {
        const tls = r.eds_campaign_tl ?? [];
        const tl = tls.find(t => t.language_id === activeLocale)   // §8: active locale first
                   ?? tls.find(t => t.language_id === 'EN_US')     // then EN_US fallback
                   ?? tls[0];
        return {
          ...r,
          campaign_name: tl?.campaign_name ?? r.internal_code,
          template_internal_code: r.eds_email_template?.internal_code ?? r.template_id,
          // §17.B4: grid must show the template's resolved name, never its raw internal_code.
          template_display_name: r.eds_email_template?.from_name_default ?? r.eds_email_template?.internal_code ?? r.template_id,
        } as EdsCampaignRow;
      });
      setRows(flat);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [activeLocale]);

  useEffect(() => {
    void reload();
    void apiDb.from('eds_campaign_status').select('*').order('sort_order')
      .then(({ data }) => setStatuses((data ?? []) as EdsCampaignStatusRow[]));
    // §17.B2: resolve each status_code to its localized name (fall back to EN_US, then code).
    const locale = activeLocale;
    void apiDb.from('eds_campaign_status_tl').select('status_code, language_id, display_text')
      .then(({ data }) => {
        type StatusTl = { status_code: string; language_id: string; display_text: string };
        const byCode: Record<string, StatusTl[]> = {};
        for (const r of (data ?? []) as StatusTl[]) (byCode[r.status_code] ??= []).push(r);
        const labels: Record<string, string> = {};
        for (const [code, tls] of Object.entries(byCode)) {
          const exact = tls.find(t => t.language_id === locale);
          const fb = tls.find(t => t.language_id === 'EN_US') ?? tls[0];
          labels[code] = exact?.display_text ?? fb?.display_text ?? code;
        }
        setStatusLabels(labels);
      });
  }, [reload, activeLocale]);

  // §17.B5: distinct facet options derived from loaded rows + the status lookup.
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      status: uniq(rows.map(r => r.status_code)).map(v => ({ value: v, label: statusLabels[v] ?? v })),
      template: uniq(rows.map(r => r.template_internal_code ?? '')).map(v => ({
        value: v,
        label: rows.find(r => r.template_internal_code === v)?.template_display_name ?? v,
      })),
    };
  }, [rows, statusLabels]);

  const facetsActive = facets.status.length + facets.template.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => {
    setFilter('');
    setFacets({ status: [], template: [] });
  };

  const filtered = useMemo(() => {
    const base = rows.filter(r => {
      const matchText = !filter.trim() ||
        r.internal_code.toLowerCase().includes(filter.toLowerCase()) ||
        (r.campaign_name ?? '').toLowerCase().includes(filter.toLowerCase());
      // OR within a facet, AND across facets.
      const matchFacets =
        (facets.status.length   === 0 || facets.status.includes(r.status_code)) &&
        (facets.template.length === 0 || facets.template.includes(r.template_internal_code ?? ''));
      return matchText && matchFacets && udcGrid.matchRow(r as unknown as Record<string, unknown>);
    });
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      if (key === 'scheduled_send_at' || key === 'completed_at') {
        return String((a as unknown as Record<string, unknown>)[key] ?? '').localeCompare(String((b as unknown as Record<string, unknown>)[key] ?? ''));
      }
      return compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? ''));
    });
  }, [rows, filter, facets, sort, udcGrid]);

  // §17.B13 related-children: the campaign's parent template (resolved name; raw code rides along as title).
  const buildRelatedGroups = (id: string): RelatedChildGroup[] => {
    const r = rows.find(x => x.campaign_id === id);
    const name = r?.template_display_name ?? r?.template_internal_code;
    return [
      { key: 'template', title: t('eds.campaigns.related.template', 'Template'),
        items: r && name ? [{ id: 'template', label: name, title: r.template_internal_code }] : [] },
    ];
  };
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const handleEnqueue = async (r: EdsCampaignRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(
      t('eds.campaigns.send_confirm', 'Send campaign "{{name}}" now?\n\nThis will queue emails to all eligible recipients.', { name: r.campaign_name ?? r.internal_code })
    )) return;

    setEnqueueing(r.campaign_id);
    setEnqueueMsg(null);
    const actor = getForensicActorId(session);
    const result = await enqueueJob(r.campaign_id, actor);
    setEnqueueing(null);

    if (result.ok) {
      setEnqueueMsg({
        id: r.campaign_id,
        ok: true,
        text: t('eds.campaigns.enqueue_success', '✓ Enqueued — {{count}} recipient(s) queued.', { count: result.recipientCount ?? 0 }),
      });
      void reload();
    } else {
      setEnqueueMsg({ id: r.campaign_id, ok: false, text: `✗ ${result.error ?? t('eds.campaigns.enqueue_failed', 'Enqueue failed.')}` });
    }
    setTimeout(() => setEnqueueMsg(null), 6000);
  };

  const handleDelete = async (r: EdsCampaignRow) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'eds_campaign', 'campaign_id', r.campaign_id);
      if (e) throw e;
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); setDeleteTarget(null); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('eds.campaigns.title', 'Campaigns')}
          description={t('eds.campaigns.list.description', 'Create, schedule and send email campaigns to your recipient lists.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('eds.campaigns.new_campaign')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('eds.campaigns.load_failed', "Couldn't load campaigns.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
                     placeholder={t('eds.campaigns.search_placeholder', 'Search campaigns…')}
                     className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('eds.campaigns.columns.status', 'Status')}     options={facetOptions.status}   selected={facets.status}   onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <FacetFilter label={t('eds.campaigns.columns.template', 'Template')}  options={facetOptions.template} selected={facets.template} onChange={v => setFacets(f => ({ ...f, template: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              {enqueueMsg && (
                <span className={`text-csm-xs ${enqueueMsg.ok ? 'text-csm-success' : 'text-csm-danger'}`}>
                  {enqueueMsg.text}
                </span>
              )}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('eds.campaigns.personalization_hint', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => {
                    if (col.key === 'template_code') {
                      return (
                        <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                            style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}>
                          {col.header}
                        </th>
                      );
                    }
                    return (
                      <SortableHeader<EdsCampaignSortKey> key={col.key}
                        sortKey={col.key as EdsCampaignSortKey} label={col.header}
                        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                        style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                    );
                  })}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const terminal = statuses.find(s => s.status_code === r.status_code)?.is_terminal === 'Y';
                  return (
                    <Fragment key={r.campaign_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton
                          open={detail.isOpen(r.campaign_id)}
                          hasChildren={hasRelated(r.campaign_id)}
                          onToggle={() => detail.toggle(r.campaign_id)}
                          expandLabel={t('common.related.show', 'Show related records')}
                          collapseLabel={t('common.related.hide', 'Hide related')}
                          tooltip={t('common.related.tooltip', 'Show related records')}
                        />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'campaign_name'    && <span className="font-medium">{r.campaign_name ?? r.internal_code}</span>}
                          {col.key === 'internal_code'    && <span className="font-mono text-csm-sm text-csm-muted">{r.internal_code}</span>}
                          {col.key === 'template_code'    && <span className="text-csm-sm text-csm-ink-soft">{r.template_display_name ?? r.template_internal_code}</span>}
                          {col.key === 'status_code'      && (
                            <StatusBadge
                              variant={STATUS_VARIANT[r.status_code] ?? 'neutral'}
                              label={statusLabels[r.status_code] ?? r.status_code}
                            />
                          )}
                          {col.key === 'scheduled_send_at' && (
                            <span className="text-csm-sm text-csm-ink-soft">
                              {r.scheduled_send_at ? new Date(r.scheduled_send_at).toLocaleString() : <span className="italic text-csm-muted">—</span>}
                            </span>
                          )}
                          {col.key === 'completed_at' && (
                            <span className="text-csm-sm text-csm-ink-soft">
                              {r.completed_at ? new Date(r.completed_at).toLocaleString() : <span className="italic text-csm-muted">—</span>}
                            </span>
                          )}
                          {udcGrid.isUdcKey(col.key) && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={e => void handleEnqueue(r, e)}
                            disabled={terminal || !['draft', 'scheduled'].includes(r.status_code) || enqueueing === r.campaign_id}
                            title={t('eds.campaigns.send_tooltip', 'Enqueue this campaign for sending')}
                            className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {enqueueing === r.campaign_id ? t('eds.campaigns.sending', 'Sending…') : t('eds.campaigns.send', '▶ Send')}
                          </button>
                          <RowActionsMenu
                            label={t('common.row_actions', 'Row actions')}
                            actions={[
                              {
                                key: 'edit',
                                label: terminal ? t('common.view', 'View') : t('common.edit'),
                                onSelect: () => setEditing(r),
                              },
                              {
                                key: 'delete',
                                label: t('common.delete'),
                                onSelect: () => setDeleteTarget(r),
                                disabled: terminal,
                                danger: true,
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                    {detail.isOpen(r.campaign_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(r.campaign_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('eds.campaigns.no_results')} action={
                      canAdd ? <button onClick={() => setCreating(true)}
                              className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                        + {t('eds.campaigns.new_campaign')}
                      </button> : undefined
                    } />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <CampaignModal
          session={session}
          campaign={editing}
          statuses={statuses}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('eds.campaigns.delete_title', 'Delete Campaign?')}
          message={t('eds.campaigns.delete_message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.campaign_name ?? deleteTarget.internal_code })}
          onConfirm={() => void handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// EDIT MODAL
// ===========================================================================

type DrawerTab = 'info' | 'translation' | 'lists';

interface CampaignModalProps {
  session: AuthSession;
  campaign: EdsCampaignRow | null;
  statuses: EdsCampaignStatusRow[];
  onClose: () => void;
  onSaved: () => void;
}

function CampaignModal({ session, campaign, statuses, onClose, onSaved }: CampaignModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  const isCreate = campaign === null;
  const [activeTab, setActiveTab] = useState<DrawerTab>('info');
  const [showDiscard, setShowDiscard] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const buildInitial = () => ({
    internalCode: campaign?.internal_code ?? '',
    templateId: campaign?.template_id ?? '',
    statusCode: campaign?.status_code ?? 'draft',
    fromNameOvr: campaign?.from_name_override ?? '',
    fromEmailOvr: campaign?.from_email_override ?? '',
    scheduledAt: campaign?.scheduled_send_at ? campaign.scheduled_send_at.slice(0, 16) : '',
    // Seeded from whatever rows the record actually has (empty for a new campaign) — NOT
    // from useLanguageOptions(). The Translation tab (below) derives its per-language
    // tabs/rows from the live active-language list at render time, so this record-identity
    // effect never needs to depend on (or re-run for) that async/live list.
    translations: [] as EdsCampaignTlForm[],
  });

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(buildInitial());
  // Custom fields placed on this page's form become first-class FieldRenderer items:
  // they interleave with native fields and inherit the same personalization.
  const udcForm = useUdcFormFields('eds_campaign', PAGE_CODE, { pkColumn: 'campaign_id', pk: campaign?.campaign_id });
  const infoCoreLayout = useMemo(() => [...CAMPAIGN_INFO_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const infoFieldDefs = useMemo(() => [...CAMPAIGN_INFO_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout: infoLayout } = useUldLayout('EDS.CAMPAIGN.EDIT.MODAL', infoCoreLayout);

  const [templates, setTemplates] = useState<EdsEmailTemplateRow[]>([]);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const terminal = statuses.find(s => s.status_code === form.statusCode)?.is_terminal === 'Y';

  // Keyed on record identity ONLY (pre-dynamic-locale behavior). Must NEVER re-run when
  // the active-language list resolves or changes — that discarded unsaved edits across
  // this whole form (regression fixed 2026-07-20). The Translation tab reads
  // useLanguageOptions() itself, live, at render time.
  useEffect(() => {
    reset(buildInitial());
    setCodeError(null);
    void apiDb.from('eds_email_template').select('template_id, internal_code, from_name_default').eq('is_active', 'Y')
      .then(({ data }) => setTemplates((data ?? []) as EdsEmailTemplateRow[]));
    if (!campaign) return;
    void apiDb.from('eds_campaign_tl').select('*').eq('campaign_id', campaign.campaign_id)
      .then(({ data }) => {
        if (!data) return;
        // Rows come straight from whatever the record has in the DB — not padded/filtered
        // to the active-language list. The Translation tab pads missing active locales
        // with an empty row at render time.
        const translations = (data as EdsCampaignTlRow[]).map(ex => ({
          language_id: ex.language_id,
          campaign_name: ex.campaign_name,
          campaign_description: ex.campaign_description ?? '',
        }));
        reset({ ...buildInitial(), translations });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!form.templateId) throw new Error(t('eds.campaigns.error_template_required', 'Template is required.'));
      let campaignId = campaign?.campaign_id ?? '';
      if (isCreate) {
        if (!form.internalCode.trim()) throw new Error(t('eds.campaigns.error_code_required', 'Internal code is required.'));
        const { row_id, error: e } = await routedInsert(session, 'eds_campaign', {
          internal_code: form.internalCode.trim().toUpperCase(),
          template_id: form.templateId,
          status_code: form.statusCode,
          from_name_override: form.fromNameOvr.trim() || null,
          from_email_override: form.fromEmailOvr.trim() || null,
          scheduled_send_at: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
        });
        if (e) throw e;
        campaignId = String(row_id);
      } else {
        const { error: e } = await routedUpdate(session, 'eds_campaign', 'campaign_id', campaignId, {
          template_id: form.templateId,
          status_code: form.statusCode,
          from_name_override: form.fromNameOvr.trim() || null,
          from_email_override: form.fromEmailOvr.trim() || null,
          scheduled_send_at: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
        });
        if (e) throw e;
      }
      for (const tl of form.translations) {
        if (!tl.campaign_name.trim()) continue;
        // FLAGGED: eds_campaign_tl has a composite PK (campaign_id, language_id); the routed
        // helpers take a single PK column. Left as a direct upsert pending a composite-key route.
        const { error: e } = await apiDb.from('eds_campaign_tl').upsert({
          campaign_id: campaignId,
          language_id: tl.language_id,
          campaign_name: tl.campaign_name,
          campaign_description: tl.campaign_description || null,
        }, { onConflict: 'campaign_id,language_id' });
        if (e) throw e;
      }
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const templateOptions = templates.map(t => ({ internal_code: t.template_id, display_text: `${t.internal_code} — ${t.from_name_default}` }));
  const statusOptions   = statuses.map(s => ({ internal_code: s.status_code, display_text: s.status_code + (s.is_terminal === 'Y' ? ' ✓' : '') }));

  const TABS: { id: DrawerTab; label: string; disabled?: boolean }[] = [
    { id: 'info',        label: t('eds.campaigns.drawer.tab_info') },
    { id: 'translation', label: t('eds.campaigns.drawer.tab_translation') },
    { id: 'lists',       label: t('eds.campaigns.drawer.tab_lists'), disabled: isCreate },
  ];

  const modalTitle = isCreate
    ? t('eds.campaigns.drawer.new_title')
    : t('eds.campaigns.drawer.edit_title', { code: campaign?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="2xl"
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as DrawerTab)}
        headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
        notice={isPersonalizing ? (
          <span>{t('eds.campaigns.drawer.personalization_hint', 'Toggle fields — hide or mark required. Click icon to save.')}</span>
        ) : undefined}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            {!terminal && activeTab !== 'lists' && (
              <button onClick={() => void save()} disabled={busy}
                      className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
                {busy ? t('common.saving') : t('common.save')}
              </button>
            )}
          </>
        }
      >

        {activeTab === 'info' && (
          <div className="space-y-3">
            {terminal && (
              <div className="rounded border border-csm-warning-border bg-csm-warning-soft px-3 py-2 text-csm-sm text-csm-warning">
                {t('eds.campaigns.terminal_warning')}
              </div>
            )}
            <FieldRenderer
              fields={infoFieldDefs}
              layout={infoLayout}
              columns={2}
              pageCode="EDS.CAMPAIGN.EDIT.MODAL"
              session={session}
              render={(f) => {
                if (f.key === 'internal_code') return (
                  <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('eds.campaigns.drawer.fields.internal_code')}>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.campaigns.drawer.fields.internal_code')}</label>
                    <input type="text" value={form.internalCode} disabled={!isCreate}
                           onChange={e => setForm(f => ({ ...f, internalCode: e.target.value.toUpperCase() }))}
                           onBlur={e => {
                             const v = e.target.value;
                             if (v && !INTERNAL_CODE_RE.test(v)) setCodeError(t('eds.campaigns.drawer.code_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                             else setCodeError(null);
                           }}
                           placeholder="EDS.CAMP.SPRING_2026"
                           className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                    {codeError
                      ? <p className="text-csm-xs text-red-600 mt-1">{codeError}</p>
                      : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('eds.campaigns.drawer.code_hint', 'E.g. EDS.CAMP.SPRING_2026 — uppercase, dots only')}</p>
                    }
                  </PersonalizedField>
                );
                if (f.key === 'status_code') return (
                  <PersonalizedField componentKey={CK} fieldKey="status_code" label={t('eds.campaigns.drawer.fields.status_code')}>
                    <FilteredModalSingleSelect label={t('eds.campaigns.drawer.fields.status_code')} options={statusOptions}
                      selectedCode={form.statusCode} onChange={c => { if (c) setForm(f => ({ ...f, statusCode: c })); }} allowClear={false} />
                  </PersonalizedField>
                );
                if (f.key === 'template') return (
                  <PersonalizedField componentKey={CK} fieldKey="template" label={t('eds.campaigns.drawer.fields.template')}>
                    <FilteredModalSingleSelect label={t('eds.campaigns.drawer.fields.template')} options={templateOptions}
                      selectedCode={form.templateId} onChange={c => { if (c) setForm(f => ({ ...f, templateId: c })); }} allowClear={false} />
                  </PersonalizedField>
                );
                if (f.key === 'from_name_override') return (
                  <PersonalizedField componentKey={CK} fieldKey="from_name_override" label={t('eds.campaigns.drawer.fields.from_name_override')}>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.campaigns.drawer.fields.from_name_override')}</label>
                    <input type="text" value={form.fromNameOvr} onChange={e => setForm(f => ({ ...f, fromNameOvr: e.target.value }))}
                           placeholder={t('eds.campaigns.drawer.override_placeholder', 'Leave blank to use template default')}
                           className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none" />
                  </PersonalizedField>
                );
                if (f.key === 'from_email_override') return (
                  <PersonalizedField componentKey={CK} fieldKey="from_email_override" label={t('eds.campaigns.drawer.fields.from_email_override')}>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.campaigns.drawer.fields.from_email_override')}</label>
                    <input type="email" value={form.fromEmailOvr} onChange={e => setForm(f => ({ ...f, fromEmailOvr: e.target.value }))}
                           placeholder={t('eds.campaigns.drawer.override_placeholder', 'Leave blank to use template default')}
                           className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none" />
                  </PersonalizedField>
                );
                if (f.key === 'scheduled_send_at') return (
                  <PersonalizedField componentKey={CK} fieldKey="scheduled_send_at" label={t('eds.campaigns.drawer.fields.scheduled_send_at')}>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.campaigns.drawer.fields.scheduled_send_at')}</label>
                    <input type="datetime-local" value={form.scheduledAt} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))}
                           className="h-8 rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none" />
                  </PersonalizedField>
                );
                if (udcForm.isUdcKey(f.key)) return (
                  <PersonalizedField componentKey={CK} fieldKey={f.key} label={f.label}
                    readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                    {udcForm.renderControl(f.key)}
                  </PersonalizedField>
                );
                return null;
              }}
            />
          </div>
        )}

        {activeTab === 'translation' && (
          <TranslationTab
            translations={form.translations}
            setTranslations={translations => setForm(f => ({ ...f, translations }))}
          />
        )}

        {activeTab === 'lists' && campaign && (
          <ListsTab session={session} campaignId={campaign.campaign_id} />
        )}

        {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1.5 text-csm-sm text-csm-danger">{error}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('eds.discard.title', 'Discard changes?')}
          message={t('eds.discard.message_lost', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('eds.discard.confirm', 'Discard')}
          cancelLabel={t('eds.discard.cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Translation tab
// ---------------------------------------------------------------------------

interface TranslationTabProps {
  translations: EdsCampaignTlForm[];
  setTranslations: (t: EdsCampaignTlForm[]) => void;
}

function TranslationTab({ translations, setTranslations }: TranslationTabProps) {
  const { t } = useTranslation();
  const { options: languageOptions } = useLanguageOptions();
  const [locale, setLocale] = useState<string>('');
  useEffect(() => {
    if (!locale && languageOptions.length > 0) setLocale(languageOptions[0].code);
  }, [locale, languageOptions]);
  const tl = translations.find(t => t.language_id === locale) ?? emptyTl(locale);
  const update = (patch: Partial<EdsCampaignTlForm>) =>
    setTranslations(
      translations.some(t => t.language_id === locale)
        ? translations.map(t => t.language_id === locale ? { ...t, ...patch } : t)
        // No row yet for this (possibly newly-active) locale — append one instead of
        // silently dropping the edit; leaves every other row/field untouched.
        : [...translations, { ...emptyTl(locale), ...patch }]
    );

  return (
    <div>
      <div className="mb-3 flex gap-1">
        {languageOptions.map(o => (
          <button key={o.code} onClick={() => setLocale(o.code)}
                  className={`rounded border px-3 py-0.5 text-csm-xs font-medium
                    ${locale === o.code ? 'border-primary bg-primary text-white' : 'border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt'}`}>
            {o.name}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.campaigns.drawer.fields.campaign_name')} <span className="text-csm-danger">*</span></label>
          <input type="text" value={tl.campaign_name} onChange={e => update({ campaign_name: e.target.value })}
                 className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.campaigns.drawer.fields.campaign_description')}</label>
          <textarea rows={4} value={tl.campaign_description} onChange={e => update({ campaign_description: e.target.value })}
                    className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-primary focus:outline-none" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lists tab  (assign recipient lists to this campaign)
// ---------------------------------------------------------------------------

interface ListsTabProps { session: AuthSession; campaignId: string; }

function ListsTab({ session: _session, campaignId }: ListsTabProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [assigned, setAssigned]   = useState<EdsRecipientListRow[]>([]);
  const [allLists, setAllLists]   = useState<EdsRecipientListRow[]>([]);
  const [selectedList, setSelectedList] = useState('');
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: campLists, error: e } = await apiDb
        .from('eds_campaign_list')
        .select(`list_id, eds_recipient_list!left(internal_code, eds_recipient_list_tl!left(list_name, language_id))`)
        .eq('campaign_id', campaignId);
      if (e) throw e;
      type ListTl = TlRowText & { list_name?: string };
      type CampListRow = {
        list_id: string;
        eds_recipient_list?: { internal_code?: string; eds_recipient_list_tl?: ListTl[] } | null;
      };
      const rows = ((campLists ?? []) as CampListRow[]).map(r => {
        const ltls = r.eds_recipient_list?.eds_recipient_list_tl ?? [];
        const tl = ltls.find(t => t.language_id === activeLocale)   // §8: active locale first
                    ?? ltls.find(t => t.language_id === 'EN_US') ?? ltls[0];
        return { list_id: r.list_id, internal_code: r.eds_recipient_list?.internal_code ?? r.list_id,
                 list_name: tl?.list_name ?? r.list_id, is_active: 'Y', created_at: '', updated_at: '', modified_by: null } as EdsRecipientListRow;
      });
      setAssigned(rows);

      const { data: all } = await apiDb
        .from('eds_recipient_list')
        .select(`list_id, internal_code, is_active, eds_recipient_list_tl!left(list_name, language_id)`)
        .eq('is_active', 'Y');
      type AllListTl = TlRowText & { list_name?: string };
      type AllListRow = EdsRecipientListRow & { eds_recipient_list_tl?: AllListTl[] };
      const allRows = ((all ?? []) as AllListRow[]).map(r => {
        const ltls = r.eds_recipient_list_tl ?? [];
        const tl = ltls.find(t => t.language_id === activeLocale)   // §8: active locale first
                   ?? ltls.find(t => t.language_id === 'EN_US') ?? ltls[0];
        return { ...r, list_name: tl?.list_name ?? r.internal_code } as EdsRecipientListRow;
      });
      setAllLists(allRows);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [campaignId, activeLocale]);

  useEffect(() => { void load(); }, [load]);

  const addList = async () => {
    if (!selectedList) return;
    setBusy(true); setError(null);
    try {
      // FLAGGED: eds_campaign_list is a composite-key junction (campaign_id + list_id) with no
      // single surrogate PK; the routed helpers take one PK column. Left as a direct insert
      // pending a composite-key route through the Common Routine.
      const { error: e } = await apiDb.from('eds_campaign_list').insert({ campaign_id: campaignId, list_id: selectedList });
      if (e) throw e;
      setSelectedList('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const removeList = async (listId: string) => {
    setBusy(true); setError(null);
    try {
      // FLAGGED: eds_campaign_list is a composite-key junction (campaign_id + list_id) with no
      // single surrogate PK; the routed helpers take one PK column. Left as a direct delete
      // pending a composite-key route through the Common Routine.
      const { error: e } = await apiDb.from('eds_campaign_list')
        .delete().eq('campaign_id', campaignId).eq('list_id', listId);
      if (e) throw e;
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const unassignedLists = allLists.filter(l => !assigned.some(a => a.list_id === l.list_id));
  const listOptions = unassignedLists.map(l => ({ internal_code: l.list_id, display_text: l.list_name ?? l.internal_code }));

  return (
    <div>
      <div className="mb-3 flex items-end gap-2">
        <div className="flex-1">
          <FilteredModalSingleSelect label={t('eds.campaigns.drawer.lists.add_list')} options={listOptions}
            selectedCode={selectedList} onChange={c => setSelectedList(c ?? '')} allowClear={false}
            placeholder={t('eds.campaigns.drawer.lists.select_placeholder', 'Select a list to add…')} />
        </div>
        <button onClick={() => void addList()} disabled={!selectedList || busy}
                className="h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-50 mb-0.5">
          {t('common.add', 'Add')}
        </button>
      </div>
      {error && <div className="mb-2 text-csm-xs text-csm-danger">{error}</div>}
      <table className="w-full border-collapse text-csm-sm">
        <thead>
          <tr className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
            <th className="border-b border-csm-border px-2 py-1.5 text-left">{t('eds.campaigns.drawer.lists.col_name', 'List Name')}</th>
            <th className="border-b border-csm-border px-2 py-1.5 text-left">{t('eds.campaigns.drawer.lists.col_code', 'Code')}</th>
            <th className="border-b border-csm-border px-2 py-1.5 text-right">{t('eds.campaigns.drawer.lists.col_action', 'Action')}</th>
          </tr>
        </thead>
        <tbody>
          {assigned.map(r => (
            <tr key={r.list_id} className="hover:bg-csm-surface-alt">
              <td className="border-b border-csm-border-soft px-2 py-1.5">{r.list_name}</td>
              <td className="border-b border-csm-border-soft px-2 py-1.5 font-mono text-csm-xs text-csm-muted">{r.internal_code}</td>
              <td className="border-b border-csm-border-soft px-2 py-1.5 text-right">
                <button onClick={() => void removeList(r.list_id)}
                        className="rounded border border-csm-danger-border bg-csm-danger-soft px-1.5 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">
                  {t('eds.campaigns.drawer.lists.remove')}
                </button>
              </td>
            </tr>
          ))}
          {assigned.length === 0 && !busy && (
            <tr><td colSpan={3} className="px-2 py-4 text-center italic text-csm-muted">{t('eds.campaigns.drawer.lists.empty')}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §17.B13 drill-down — this page owns the campaign editor and registers it for
// other pages' related-children panels (module-load side effect).
// ---------------------------------------------------------------------------
function CampaignChildEditor({ session, pk, onClose, onSaved }: ChildEditorProps) {
  const { t } = useTranslation();
  const [row, setRow] = useState<EdsCampaignRow | null>(null);
  const [statuses, setStatuses] = useState<EdsCampaignStatusRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // The modal needs the status options as well as the row — the same two
      // reads the list page performs, scoped to the one campaign.
      const [{ data: cData, error: cErr }, { data: sData }] = await Promise.all([
        apiDb.from('eds_campaign').select('*').eq('campaign_id', pk).maybeSingle(),
        apiDb.from('eds_campaign_status').select('*').order('sort_order'),
      ]);
      if (cancelled) return;
      if (cErr) setError(cErr.message);
      else setRow((cData as EdsCampaignRow) ?? null);
      setStatuses((sData ?? []) as EdsCampaignStatusRow[]);
      setBusy(false);
    })();
    return () => { cancelled = true; };
  }, [pk]);

  if (busy || error || !row) {
    return (
      <Modal title={t('eds.campaigns.edit_title', 'Campaign')} onClose={onClose} width="lg">
        <div className={'px-1 py-3 text-csm-sm ' + (error ? 'text-csm-danger' : 'text-csm-muted')}>
          {error ?? (busy ? t('common.loading', 'Loading…') : t('common.not_found', 'Record not found.'))}
        </div>
      </Modal>
    );
  }
  return <CampaignModal session={session} campaign={row} statuses={statuses} onClose={onClose} onSaved={onSaved} />;
}

registerChildEditor('eds_campaign', {
  pageCode: PAGE_CODE,
  title: 'Campaign',
  Editor: CampaignChildEditor,
});
